import { EUserRole } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';

/**
 * Roles that the active user can assign/manage, driven only by its own capabilities.
 *
 * - `CRUD_USERS_CARRIER`: drivers and carrier admins.
 * - `CRUD_USERS_DECA_CARRIER`: DeCA-as-carrier and DeCA drivers.
 * - `CRUD_USERS_SHIPPER`: other shipper admins.
 * - `CRUD_USERS_DECA_SHIPPER`: other DeCA-as-shipper users.
 */
export const MANAGED_ROLES_BY_CAPABILITY: Partial<Record<ECapabilities, readonly EUserRole[]>> = {
  [ECapabilities.CRUD_USERS_CARRIER]: [EUserRole.DRIVER, EUserRole.CARRIER_ADMIN],
  [ECapabilities.CRUD_USERS_DECA_CARRIER]: [EUserRole.CREATE_DECA_AS_CARRIER, EUserRole.DECA_DRIVER],
  [ECapabilities.CRUD_USERS_SHIPPER]: [EUserRole.SHIPPER_ADMIN],
  [ECapabilities.CRUD_USERS_DECA_SHIPPER]: [EUserRole.CREATE_DECA_AS_SHIPPER],
};

/** Union of the roles that a user with `capabilities` can assign. */
export function manageableRolesFor(capabilities: ECapabilities[]): EUserRole[] {
  const manageable = new Set<EUserRole>();

  capabilities.forEach((capability) =>
    (MANAGED_ROLES_BY_CAPABILITY[capability] ?? []).forEach((role) => manageable.add(role)),
  );

  return [...manageable];
}

/**
 * The active user can only delete or disable a user when all of its roles are manageable; otherwise
 * it could remove a user that holds permissions it is not authorized to manage.
 */
export function canManageTargetRoles(manageableRoles: EUserRole[], targetRoles: EUserRole[]): boolean {
  return targetRoles.every((role) => manageableRoles.includes(role));
}
